import { useState, useRef, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Compass, Shield, Skull, BookOpen, Globe, Check, ChevronDown } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";

const NAV = [
  { to: "/", key: "nav.strategist", icon: Compass, end: true, testid: "nav-strategist" },
  { to: "/generals", key: "nav.generals", icon: Shield, testid: "nav-generals" },
  { to: "/monsters", key: "nav.monsters", icon: Skull, testid: "nav-monsters" },
  { to: "/guides", key: "nav.guides", icon: BookOpen, testid: "nav-guides" },
];

function LanguageSelector() {
  const { lang, setLang, languages, t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find((l) => l.code === lang);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="es-lang" ref={ref}>
      <button className="es-lang-btn" data-testid="language-selector-button"
        aria-haspopup="listbox" aria-expanded={open} aria-label={t("nav.language")}
        onClick={() => setOpen((o) => !o)}>
        <Globe size={16} />
        <span className="es-lang-code">{current?.native}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <ul className="es-lang-menu" role="listbox" data-testid="language-menu">
          {languages.map((l) => (
            <li key={l.code}>
              <button role="option" aria-selected={l.code === lang}
                className={`es-lang-item ${l.code === lang ? "active" : ""}`}
                data-testid={`language-option-${l.code}`}
                onClick={() => { setLang(l.code); setOpen(false); }}>
                <span className="es-lang-native">{l.native}</span>
                <span className="es-lang-abbr">{l.code}</span>
                {l.code === lang && <Check size={14} className="es-lang-check" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { t, dir } = useLang();
  return (
    <div className="es-app" data-testid="app-root" dir={dir}>
      <aside className="es-sidebar" data-testid="sidebar">
        <Link to="/" className="es-brand" data-testid="brand-home-link">
          <Wordmark />
        </Link>
        <nav className="es-nav" aria-label="Primary">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({ isActive }) => `es-nav-link ${isActive ? "active" : ""}`}>
                <Icon size={18} /> <span>{t(n.key)}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="es-sidebar-foot">
          <div className="es-lang-wrap">
            <span className="es-lang-label">{t("nav.language")}</span>
            <LanguageSelector />
          </div>
          <p className="es-unofficial">{t("brand.unofficial")}</p>
        </div>
      </aside>

      <header className="es-topbar" data-testid="topbar">
        <Link to="/" className="es-brand-compact" data-testid="brand-home-link-mobile">
          <Wordmark compact />
          <span className="es-topbar-name">EVONY STRATEGIST</span>
        </Link>
        <LanguageSelector />
      </header>

      <main className="es-main" data-testid="main-content">{children}</main>

      <nav className="es-bottomnav" aria-label="Primary mobile" data-testid="bottom-nav">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`${n.testid}-mobile`}
              className={({ isActive }) => `es-bottomnav-link ${isActive ? "active" : ""}`}>
              <Icon size={20} /> <span>{t(n.key)}</span>
            </NavLink>
          );
        })}
      </nav>

      <footer className="es-footer" data-testid="footer">
        <p>{t("brand.unofficial")}</p>
        <p className="es-footer-muted">Data sourced from the Evony Guide Wiki · Evony Strategist is an independent project.</p>
      </footer>
    </div>
  );
}
